import React, { useState, useEffect, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { controlCenterApi, type AccountDetail, type FeatureItem } from '../../services/controlCenterApi'

interface AccountContext {
  account: AccountDetail
  refreshAccount: () => void
}

interface CategoryGroup {
  category: string
  features: FeatureItem[]
}

const CATEGORY_LABELS: Record<string, string> = {
  finance: 'Finance',
  inventory: 'Inventory',
  listings: 'Listings',
  integrations: 'Integrations',
  photos: 'Photos',
  general: 'General',
}

export function AccountFeaturesTab(): React.ReactElement {
  const { account } = useOutletContext<AccountContext>()
  const [features, setFeatures] = useState<FeatureItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [toggling, setToggling] = useState<Set<string>>(new Set())

  const fetchFeatures = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const data = await controlCenterApi.getAccountFeatures(account.id)
      setFeatures(data)
    } catch {
      setError('Failed to load features')
    }
    setIsLoading(false)
  }, [account.id])

  useEffect(() => {
    fetchFeatures()
  }, [fetchFeatures])

  const handleToggle = async (feature: FeatureItem) => {
    // Optimistic update
    setToggling(prev => new Set([...prev, feature.id]))
    setFeatures(prev =>
      prev.map(f => f.id === feature.id ? { ...f, enabled: !f.enabled } : f)
    )

    try {
      await controlCenterApi.toggleFeature(account.id, feature.id)
    } catch {
      // Rollback on error
      setFeatures(prev =>
        prev.map(f => f.id === feature.id ? { ...f, enabled: !f.enabled } : f)
      )
      setError('Failed to toggle feature')
    }
    setToggling(prev => {
      const next = new Set(prev)
      next.delete(feature.id)
      return next
    })
  }

  // Group features by category
  const grouped: CategoryGroup[] = features.reduce<CategoryGroup[]>((acc, feature) => {
    const existing = acc.find(g => g.category === feature.category)
    if (existing) {
      existing.features.push(feature)
    } else {
      acc.push({ category: feature.category, features: [feature] })
    }
    return acc
  }, [])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading features...</span>
      </div>
    )
  }

  if (error && features.length === 0) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <p className="text-red-300 text-sm">{error}</p>
        <button type="button" onClick={fetchFeatures} className="text-red-400 hover:text-red-300 text-sm mt-2 underline">
          Retry
        </button>
      </div>
    )
  }

  const enabledCount = features.filter(f => f.enabled).length

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Feature Flags</h2>
          <p className="text-slate-400 text-sm mt-0.5">
            {enabledCount} of {features.length} features enabled
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Feature groups */}
      <div className="space-y-6">
        {grouped.map(group => (
          <div key={group.category}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              {CATEGORY_LABELS[group.category] || group.category}
            </h3>
            <div className="bg-slate-800 rounded-xl border border-slate-700 divide-y divide-slate-700/50">
              {group.features.map(feature => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between px-5 py-4"
                >
                  <div className="min-w-0 flex-1 mr-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{feature.name}</span>
                      {feature.is_default && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary">
                          Default
                        </span>
                      )}
                    </div>
                    {feature.description && (
                      <p className="text-xs text-slate-400 mt-0.5">{feature.description}</p>
                    )}
                  </div>

                  {/* Toggle switch */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={feature.enabled}
                    aria-label={`Toggle ${feature.name}`}
                    onClick={() => handleToggle(feature)}
                    disabled={toggling.has(feature.id)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 ${
                      feature.enabled ? 'bg-primary' : 'bg-slate-600'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        feature.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {features.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-400 text-sm">No features configured</p>
        </div>
      )}
    </div>
  )
}
